import React, { useMemo } from 'react';
import { Button } from './Button';

export type PaginationProps = Readonly<{
  total: number;
  page: number; // 1-based
  pageSize: number;
  onChange: (page: number) => void;
  className?: string;
}>;

export function Pagination({ total, page, pageSize, onChange, className }: PaginationProps) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const canPrev = page > 1;
  const canNext = page < totalPages;

  const pageNumbers = useMemo(() => {
    if (totalPages <= 1) return [] as number[]; // keine Buttons nötig
    const pages: number[] = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      const add = (p: number) => { if (!pages.includes(p)) pages.push(p); };
      add(1);
      if (page > 3) add(page - 2);
      if (page > 2) add(page - 1);
      add(page);
      if (page < totalPages - 1) add(page + 1);
      if (page < totalPages - 2) add(page + 2);
      add(totalPages);
      pages.sort((a,b) => a-b);
    }
    return pages;
  }, [page, totalPages]);

  return (
    <div className={"flex items-center justify-between mt-3 text-sm " + (className || '')} data-testid="pagination">
      <div className="text-slate-600" data-testid="page-info">Seite {page} von {totalPages}</div>
      {totalPages > 1 && (
        <div className="flex items-center gap-2" data-testid="page-controls">
          <Button variant="ghost" disabled={!canPrev} onClick={() => canPrev && onChange(page - 1)} data-testid="prev-page-btn">Zurück</Button>
          {pageNumbers.map(p => (
            <Button
              key={p}
              variant={p === page ? 'primary' : 'ghost'}
              onClick={() => onChange(p)}
              data-testid={`page-btn-${p}`}
            >{p}</Button>
          ))}
          <Button variant="ghost" disabled={!canNext} onClick={() => canNext && onChange(page + 1)} data-testid="next-page-btn">Weiter</Button>
        </div>
      )}
    </div>
  );
}
